// Shared surface-path geometry (taxgw-2ybg A2). Land triangles/corridors live in an unwrapped Mercator chart, so a
// straight chart segment between two of their vertices is a RHUMB segment on the globe — not a great-circle shortcut
// that would bow back over water. Sea legs keep great-circle segments. Behaviour is selected through the strategy
// registry below, never a growing type switch in a consumer.
//
// A SurfacePath is pure data: `[latDegrees, lonDegrees]` control points, serializable, no Three/ECS. A rhumb path may
// carry longitudes unwrapped across the date line (explicitly NOT the minor longitude delta); a great-circle path
// uses ordinary normalized longitudes. World output poses normalize longitude; the stored unwrapped source deltas
// are never rewritten.

import { InvariantViolation } from '@taxwar/content/geometry/invariants'
import { DEG_TO_RAD, RAD_TO_DEG, angleBetweenLatLon } from '@taxwar/content/geometry/sphere'

export type SurfacePathKind = 'greatCircle' | 'rhumb'

/** [latitudeDegrees, longitudeDegrees]. A rhumb longitude may be unwrapped past ±180. */
export type LatLon = readonly [number, number]

/** Coordinate-roundoff agreement scale (degrees) for two coordinates that name the same endpoint; never metres. */
export const ENDPOINT_ROUNDOFF_DEG = 1e-9
const FULL_TURN_DEG = 360

/** Whether two latitudes name the same place within coordinate roundoff. */
export function sameLatitude(a: number, b: number): boolean {
  return Math.abs(a - b) <= ENDPOINT_ROUNDOFF_DEG
}

/** Whether two longitudes name the same meridian modulo a whole 360° turn, within coordinate roundoff. */
export function sameLongitudeModuloTurn(a: number, b: number): boolean {
  const delta = (((a - b) % FULL_TURN_DEG) + FULL_TURN_DEG) % FULL_TURN_DEG
  return delta <= ENDPOINT_ROUNDOFF_DEG || FULL_TURN_DEG - delta <= ENDPOINT_ROUNDOFF_DEG
}

/**
 * Whether two coordinates name the same endpoint: equal latitude and equal longitude modulo a whole 360° turn. A
 * baked land lane stores endpoint longitudes unwrapped by an integer turn, so this is the ONE place that decides
 * whether a corridor junction agrees — at coordinate roundoff, never a metres distance.
 */
export function sameEndpoint(a: LatLon, b: LatLon): boolean {
  return sameLatitude(a[0], b[0]) && sameLongitudeModuloTurn(a[1], b[1])
}

export interface SurfacePath {
  readonly kind: SurfacePathKind
  readonly points: readonly LatLon[]
}

/** A point along a path; heading is clockwise from north, radians in (-π, π]. */
export interface PathPose {
  lat: number
  lon: number
  heading: number
}

/** Mercator latitude is singular at the poles; reject |lat| closer than this to ±90°. */
export const MERCATOR_POLE_MARGIN_RAD = 1e-12
/** Segments shorter than this are degenerate: callers must not emit them. */
const SEGMENT_EPSILON_RAD = 1e-15
/** Great-circle segments closer than this to π (the antipode) are undefined and rejected at preparation. */
const ANTIPODAL_EPSILON_RAD = 1e-9

const TWO_PI = Math.PI * 2

/** Wrap an angle to (-π, π]. */
function wrapPi(angle: number): number {
  let x = angle % TWO_PI
  if (x <= -Math.PI) x += TWO_PI
  else if (x > Math.PI) x -= TWO_PI
  return x
}

/** Wrap a longitude in degrees to (-180, 180]. World output only; source deltas stay unwrapped. */
function normalizeLon(lonDeg: number): number {
  let lon = lonDeg % 360
  if (lon <= -180) lon += 360
  else if (lon > 180) lon -= 360
  return lon
}

/** Initial great-circle bearing from one point to another (radians, clockwise from north). */
function initialBearing(fromLatRad: number, fromLonRad: number, toLatRad: number, toLonRad: number): number {
  const dLon = toLonRad - fromLonRad
  const y = Math.sin(dLon) * Math.cos(toLatRad)
  const x = Math.cos(fromLatRad) * Math.sin(toLatRad) - Math.sin(fromLatRad) * Math.cos(toLatRad) * Math.cos(dLon)
  return Math.atan2(y, x)
}

/** Mercator chart ordinate y = ln(tan(π/4 + φ/2)) for a latitude in radians. Singular at the poles. */
export function mercatorY(latRad: number): number {
  if (!(Math.abs(latRad) < Math.PI / 2 - MERCATOR_POLE_MARGIN_RAD)) {
    throw new InvariantViolation(`rhumb: latitude ${latRad * RAD_TO_DEG}° is at the pole`)
  }
  return Math.log(Math.tan(Math.PI / 4 + latRad / 2))
}

/** Inverse of mercatorY: latitude in radians from a chart ordinate. */
export function inverseMercatorY(y: number): number {
  return 2 * Math.atan(Math.exp(y)) - Math.PI / 2
}

/** Angular length of the rhumb segment a→b, with the great-circle distance as a roundoff lower bound. */
function rhumbLengthRad(a: LatLon, b: LatLon): number {
  const phi1 = a[0] * DEG_TO_RAD
  const phi2 = b[0] * DEG_TO_RAD
  const dPhi = phi2 - phi1
  const dLam = (b[1] - a[1]) * DEG_TO_RAD
  if (Math.abs(dPhi) < SEGMENT_EPSILON_RAD && Math.abs(dLam) < SEGMENT_EPSILON_RAD) {
    throw new InvariantViolation('rhumb: zero-length segment')
  }
  let length: number
  if (Math.abs(dPhi) < SEGMENT_EPSILON_RAD) {
    // Continuous east-west limit of q = Δφ/ΔY: cos(φ).
    length = Math.hypot(dPhi, Math.cos(phi1) * dLam)
  } else {
    const dY = mercatorY(phi2) - mercatorY(phi1)
    const q = dPhi / dY
    length = Math.hypot(dPhi, q * dLam)
  }
  // The great circle is the shortest surface path, so a rhumb is never shorter; only roundoff can make it seem so.
  return Math.max(length, angleBetweenLatLon(a[0], a[1], b[0], b[1]))
}

/** Behaviour of one path kind: validate a control point, measure a segment, and sample it at a distance fraction. */
export interface SurfacePathStrategy {
  validatePoint(latDeg: number, lonDeg: number, what: string): void
  segmentLengthRad(a: LatLon, b: LatLon): number
  sampleSegment(a: LatLon, b: LatLon, t: number, out: PathPose): void
}

function validateLatLon(latDeg: number, lonDeg: number, what: string): void {
  if (!Number.isFinite(latDeg) || !Number.isFinite(lonDeg)) throw new InvariantViolation(`${what}: non-finite coordinate`)
  if (Math.abs(latDeg) > 90) throw new InvariantViolation(`${what}: latitude ${latDeg}° is outside ±90`)
}

const GREAT_CIRCLE: SurfacePathStrategy = {
  validatePoint(latDeg, lonDeg, what): void {
    validateLatLon(latDeg, lonDeg, what)
  },
  segmentLengthRad(a, b): number {
    const length = angleBetweenLatLon(a[0], a[1], b[0], b[1])
    // Positive, non-antipodal length is part of the path contract; reject at preparation, not during movement.
    if (!(length > SEGMENT_EPSILON_RAD) || !Number.isFinite(length)) throw new InvariantViolation('greatCircle: zero-length or non-finite segment')
    if (Math.PI - length < ANTIPODAL_EPSILON_RAD) throw new InvariantViolation('greatCircle: antipodal segment has no unique minor arc')
    return length
  },
  sampleSegment(a, b, t, out): void {
    // Exact endpoints: the caller asked for the stored coordinate, not an inverse-trig reconstruction of it.
    if (t <= 0) {
      out.lat = a[0]
      out.lon = normalizeLon(a[1])
      out.heading = initialBearing(a[0] * DEG_TO_RAD, a[1] * DEG_TO_RAD, b[0] * DEG_TO_RAD, b[1] * DEG_TO_RAD)
      return
    }
    if (t >= 1) {
      out.lat = b[0]
      out.lon = normalizeLon(b[1])
      out.heading = wrapPi(initialBearing(b[0] * DEG_TO_RAD, b[1] * DEG_TO_RAD, a[0] * DEG_TO_RAD, a[1] * DEG_TO_RAD) + Math.PI)
      return
    }
    // Scalar unit-vector components: no per-sample array allocation.
    const phiA = a[0] * DEG_TO_RAD
    const lamA = a[1] * DEG_TO_RAD
    const phiB = b[0] * DEG_TO_RAD
    const lamB = b[1] * DEG_TO_RAD
    const cosPhiA = Math.cos(phiA)
    const cosPhiB = Math.cos(phiB)
    const ax = cosPhiA * Math.cos(lamA)
    const ay = cosPhiA * Math.sin(lamA)
    const az = Math.sin(phiA)
    const bx = cosPhiB * Math.cos(lamB)
    const by = cosPhiB * Math.sin(lamB)
    const bz = Math.sin(phiB)
    const theta = angleBetweenLatLon(a[0], a[1], b[0], b[1])
    const sinTheta = Math.sin(theta)
    if (!(sinTheta > SEGMENT_EPSILON_RAD)) throw new InvariantViolation('greatCircle: coincident or antipodal segment')
    const wa = Math.sin((1 - t) * theta) / sinTheta
    const wb = Math.sin(t * theta) / sinTheta
    const x = wa * ax + wb * bx
    const y = wa * ay + wb * by
    const z = wa * az + wb * bz
    const latRad = Math.asin(Math.min(1, Math.max(-1, z)))
    const lonRad = Math.atan2(y, x)
    out.lat = latRad * RAD_TO_DEG
    out.lon = normalizeLon(lonRad * RAD_TO_DEG)
    out.heading = initialBearing(latRad, lonRad, phiB, lamB)
  },
}

/** Constant heading of the rhumb segment a→b (radians, clockwise from north). */
function rhumbHeading(a: LatLon, b: LatLon): number {
  const dPhi = (b[0] - a[0]) * DEG_TO_RAD
  const dLam = (b[1] - a[1]) * DEG_TO_RAD
  if (Math.abs(dPhi) < SEGMENT_EPSILON_RAD) return Math.atan2(dLam, 0)
  const dY = mercatorY(b[0] * DEG_TO_RAD) - mercatorY(a[0] * DEG_TO_RAD)
  return Math.atan2(dLam, dY)
}

const RHUMB: SurfacePathStrategy = {
  validatePoint(latDeg, lonDeg, what): void {
    validateLatLon(latDeg, lonDeg, what)
    // Reject a pole even on the east-west branch, where the normal march never calls mercatorY.
    if (!(Math.abs(latDeg * DEG_TO_RAD) < Math.PI / 2 - MERCATOR_POLE_MARGIN_RAD)) {
      throw new InvariantViolation(`${what}: rhumb point ${latDeg}° is at the pole`)
    }
  },
  segmentLengthRad: (a, b) => rhumbLengthRad(a, b),
  sampleSegment(a, b, t, out): void {
    if (t <= 0) {
      out.lat = a[0]
      out.lon = normalizeLon(a[1])
      out.heading = rhumbHeading(a, b)
      return
    }
    if (t >= 1) {
      out.lat = b[0]
      out.lon = normalizeLon(b[1])
      out.heading = rhumbHeading(a, b)
      return
    }
    const phi1 = a[0] * DEG_TO_RAD
    const phi2 = b[0] * DEG_TO_RAD
    const lam1 = a[1] * DEG_TO_RAD
    const dPhi = phi2 - phi1
    const dLam = (b[1] - a[1]) * DEG_TO_RAD
    if (Math.abs(dPhi) < SEGMENT_EPSILON_RAD && Math.abs(dLam) < SEGMENT_EPSILON_RAD) {
      throw new InvariantViolation('rhumb: zero-length segment')
    }
    if (Math.abs(dPhi) < SEGMENT_EPSILON_RAD) {
      // Constant latitude: longitude (and distance) advance linearly; heading is due east or west.
      out.lat = a[0] + t * (b[0] - a[0])
      out.lon = normalizeLon((lam1 + t * dLam) * RAD_TO_DEG)
      out.heading = Math.atan2(dLam, 0)
      return
    }
    const y1 = mercatorY(phi1)
    const y2 = mercatorY(phi2)
    const dY = y2 - y1
    // Latitude is linear in distance fraction; longitude advances with the change in Mercator y.
    const phi = phi1 + t * dPhi
    const y = mercatorY(phi)
    out.lat = phi * RAD_TO_DEG
    out.lon = normalizeLon((lam1 + (dLam / dY) * (y - y1)) * RAD_TO_DEG)
    out.heading = Math.atan2(dLam, dY)
  },
}

const STRATEGIES: Readonly<Record<SurfacePathKind, SurfacePathStrategy>> = {
  greatCircle: GREAT_CIRCLE,
  rhumb: RHUMB,
}

export function surfacePathStrategy(kind: SurfacePathKind): SurfacePathStrategy {
  // Own-key lookup: a raw string like "toString" or "__proto__" must not resolve an inherited object.
  const strategy = Object.prototype.hasOwnProperty.call(STRATEGIES, kind) ? STRATEGIES[kind] : undefined
  if (strategy === undefined) throw new InvariantViolation(`surface path: unknown kind ${String(kind)}`)
  return strategy
}

/**
 * A path prepared for measurement and sampling: an immutable owned copy of the source, positive strictly
 * increasing per-segment cumulative lengths, and the total angular length. `sample` binary-searches the segment
 * and writes into a caller-owned pose — no per-sample allocation.
 */
export class PreparedPath {
  readonly source: SurfacePath
  /** Read-only view of the prefix lengths: cumulative[i] is the distance from points[0] to points[i]; cumulative[0] is 0, strictly increasing. */
  readonly cumulative: ArrayLike<number>
  readonly lengthRad: number
  private readonly cumulativeStorage: Float64Array
  private readonly strategy: SurfacePathStrategy

  constructor(path: SurfacePath) {
    const strategy = surfacePathStrategy(path.kind)
    const input = path.points
    if (input.length < 2) throw new InvariantViolation(`PreparedPath: ${input.length} point(s); a path needs at least two`)
    const points: LatLon[] = []
    for (let i = 0; i < input.length; i++) {
      const point = input[i] as LatLon
      strategy.validatePoint(point[0], point[1], `PreparedPath point ${i}`)
      points.push(Object.freeze([point[0], point[1]]) as LatLon)
    }
    // Own an immutable copy so the cumulative metric can never silently go stale under a mutated caller array.
    this.source = Object.freeze({ kind: path.kind, points: Object.freeze(points) })
    this.strategy = strategy
    const cumulative = new Float64Array(points.length)
    let total = 0
    for (let i = 0; i + 1 < points.length; i++) {
      const segment = strategy.segmentLengthRad(points[i] as LatLon, points[i + 1] as LatLon)
      if (!(segment > 0) || !Number.isFinite(segment)) throw new InvariantViolation(`PreparedPath: segment ${i} has non-positive or non-finite length`)
      total += segment
      if (!(total > (cumulative[i] as number))) throw new InvariantViolation('PreparedPath: cumulative lengths are not strictly increasing')
      cumulative[i + 1] = total
    }
    if (!(total > 0) || !Number.isFinite(total)) throw new InvariantViolation('PreparedPath: non-positive or non-finite total length')
    this.cumulativeStorage = cumulative
    this.cumulative = cumulative
    this.lengthRad = total
  }

  /** Writes the pose at `fraction` ∈ [0, 1] of the total length (clamped) into `out`. */
  sample(fraction: number, out: PathPose): PathPose {
    const clamped = fraction <= 0 ? 0 : fraction >= 1 ? 1 : fraction
    const target = clamped * this.lengthRad
    const cumulative = this.cumulativeStorage
    const points = this.source.points
    let lo = 0
    let hi = points.length - 1
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1
      if ((cumulative[mid] as number) <= target) lo = mid
      else hi = mid
    }
    const span = (cumulative[lo + 1] as number) - (cumulative[lo] as number)
    const t = (target - (cumulative[lo] as number)) / span
    this.strategy.sampleSegment(points[lo] as LatLon, points[lo + 1] as LatLon, t, out)
    return out
  }
}

/** The same geometry travelled backwards, preserving the intended (possibly unwrapped) points and metric. */
export function reverseSurfacePath(path: SurfacePath): SurfacePath {
  return { kind: path.kind, points: [...path.points].reverse() }
}

/**
 * Visits every control point and subdivides each leg so no step exceeds `maxStepRad`. Sampling the whole path
 * uniformly would cut corners at control points; this walks leg by leg instead. Every control point is emitted
 * exactly (the segment endpoints are the stored coordinates) with a normalized longitude. `alongRad` is the
 * distance from the path start.
 */
export function forEachPathSample(
  path: SurfacePath,
  maxStepRad: number,
  visit: (latDeg: number, lonDeg: number, alongRad: number) => void,
): void {
  if (!(maxStepRad > 0)) throw new InvariantViolation(`forEachPathSample: step must be positive, got ${maxStepRad}`)
  const strategy = surfacePathStrategy(path.kind)
  const points = path.points
  if (points.length < 2) throw new InvariantViolation(`forEachPathSample: ${points.length} point(s); a path needs at least two`)
  const pose: PathPose = { lat: 0, lon: 0, heading: 0 }
  let along = 0
  for (let i = 0; i + 1 < points.length; i++) {
    const a = points[i] as LatLon
    const b = points[i + 1] as LatLon
    const segLength = strategy.segmentLengthRad(a, b)
    const steps = Math.max(1, Math.ceil(segLength / maxStepRad))
    for (let s = i === 0 ? 0 : 1; s <= steps; s++) {
      strategy.sampleSegment(a, b, s / steps, pose)
      visit(pose.lat, pose.lon, along + (segLength * s) / steps)
    }
    along += segLength
  }
}
